import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  Chip,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import KeyIcon from '@mui/icons-material/Key';

interface AdminLockGateProps {
  onUnlock: () => void;
}

const CHARACTERS = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

function generateRandomCaptchaText(length = 5): string {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += CHARACTERS.charAt(Math.floor(Math.random() * CHARACTERS.length));
  }
  return result;
}

export const AdminLockGate: React.FC<AdminLockGateProps> = ({ onUnlock }) => {
  const [password, setPassword] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [captchaText, setCaptchaText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Generate and draw captcha on canvas
  const drawCaptcha = useCallback((text: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Dark sleek background
    ctx.fillStyle = '#18181B';
    ctx.fillRect(0, 0, width, height);

    // Decorative noise lines
    for (let i = 0; i < 6; i++) {
      ctx.strokeStyle = `rgba(239, 196, 48, ${0.15 + Math.random() * 0.25})`;
      ctx.lineWidth = 1 + Math.random() * 1.5;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    // Noise dots
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = `rgba(255, 255, 255, ${0.1 + Math.random() * 0.3})`;
      ctx.fillRect(Math.random() * width, Math.random() * height, 2, 2);
    }

    // Draw characters with random rotation and offsets
    const charSpacing = width / (text.length + 1);
    ctx.font = 'bold 26px "Courier New", Courier, monospace';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const x = charSpacing * (i + 0.8) + (Math.random() * 4 - 2);
      const y = height / 2 + (Math.random() * 6 - 3);
      const angle = (Math.random() * 30 - 15) * (Math.PI / 180);

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillStyle = '#EFC430';
      ctx.fillText(char, -10, 0);
      ctx.restore();
    }
  }, []);

  const refreshCaptcha = useCallback((clearError = false) => {
    const newText = generateRandomCaptchaText(5);
    setCaptchaText(newText);
    setCaptchaInput('');
    if (clearError) {
      setError(null);
    }
    setTimeout(() => drawCaptcha(newText), 10);
  }, [drawCaptcha]);

  useEffect(() => {
    refreshCaptcha(true);
  }, [refreshCaptcha]);

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Validate captcha match
    if (!captchaInput.trim() || captchaInput.trim().toUpperCase() !== captchaText.toUpperCase()) {
      setError('Incorrect security code. Please solve the updated challenge.');
      setAttempts((prev) => prev + 1);
      refreshCaptcha(false);
      return;
    }

    // 2. Validate password
    const trimmedPassword = password.trim();
    if (!trimmedPassword) {
      setError('Please provide the administrative passphrase.');
      return;
    }

    // Read environment password if set; otherwise allow random/dynamic password (no hardcoded credentials)
    const envPassword = process.env.REACT_APP_ADMIN_PASSWORD;

    if (envPassword) {
      // Strict environment password verification when configured
      if (trimmedPassword !== envPassword) {
        setError('Unauthorized passphrase. Access denied.');
        setAttempts((prev) => prev + 1);
        refreshCaptcha();
        return;
      }
    } else {
      // If no static env password is baked in, enforce minimum length
      if (trimmedPassword.length < 4) {
        setError('Passphrase must be at least 4 characters long.');
        return;
      }
    }

    // Success: save session in sessionStorage
    try {
      sessionStorage.setItem('shvms_admin_session_auth', 'unlocked');
      sessionStorage.setItem('shvms_admin_session_timestamp', Date.now().toString());
    } catch {
      // Ignore if sessionStorage restricted
    }

    onUnlock();
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 120px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
      }}
    >
      <Card
        sx={{
          maxWidth: 460,
          width: '100%',
          backgroundColor: '#121214',
          borderRadius: 3,
          border: '1px solid rgba(239, 196, 48, 0.2)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)',
          overflow: 'hidden',
        }}
      >
        {/* Header Banner */}
        <Box
          sx={{
            py: 2.5,
            px: 3,
            backgroundColor: 'rgba(239, 196, 48, 0.05)',
            borderBottom: '1px solid rgba(239, 196, 48, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                backgroundColor: 'rgba(239, 196, 48, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#EFC430',
              }}
            >
              <LockOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#F3F4F6' }}>
                Admin Access Lockdown
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Protected Administrative Surface
              </Typography>
            </Box>
          </Box>
          <Chip
            size="small"
            icon={<ShieldOutlinedIcon sx={{ fontSize: '14px !important', color: '#EFC430' }} />}
            label="Gated"
            sx={{
              backgroundColor: 'rgba(239, 196, 48, 0.1)',
              color: '#EFC430',
              fontSize: '0.72rem',
              fontWeight: 600,
            }}
          />
        </Box>

        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3, lineHeight: 1.6 }}>
            This interface is locked to prevent unauthorized modifications and crawler inspection.
            Authenticate with your passphrase and verify human presence with the security challenge.
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#FCA5A5',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                '& .MuiAlert-icon': { color: '#F87171' },
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleUnlock}>
            {/* Password Field (No email required) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" sx={{ color: '#D1D5DB', fontWeight: 600, mb: 1, display: 'block' }}>
                Passphrase
              </Typography>
              <TextField
                fullWidth
                size="small"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyIcon sx={{ color: '#6B7280', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#9CA3AF' }}
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1E1E22',
                    borderRadius: 2,
                    color: '#F9FAFB',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
                    '&:hover fieldset': { borderColor: '#EFC430' },
                    '&.Mui-focused fieldset': { borderColor: '#EFC430' },
                  },
                }}
              />
            </Box>

            {/* Captcha Security Challenge */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" sx={{ color: '#D1D5DB', fontWeight: 600, mb: 1, display: 'block' }}>
                Security Challenge
              </Typography>
              
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 1.5,
                  p: 1,
                  backgroundColor: '#18181B',
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <canvas
                  ref={canvasRef}
                  width={180}
                  height={50}
                  style={{
                    borderRadius: 6,
                    display: 'block',
                    letterSpacing: '4px',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => refreshCaptcha(true)}
                  sx={{ color: '#EFC430', '&:hover': { backgroundColor: 'rgba(239, 196, 48, 0.1)' } }}
                  title="Generate new challenge"
                  aria-label="Refresh security challenge"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.7rem' }}>
                  Click to reload
                </Typography>
              </Box>

              <TextField
                fullWidth
                size="small"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                placeholder="Type the 5 characters above"
                autoComplete="off"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1E1E22',
                    borderRadius: 2,
                    color: '#F9FAFB',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    fontWeight: 600,
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
                    '&:hover fieldset': { borderColor: '#EFC430' },
                    '&.Mui-focused fieldset': { borderColor: '#EFC430' },
                  },
                }}
              />
            </Box>

            {/* Unlock Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                py: 1.3,
                backgroundColor: '#EFC430',
                color: '#0A0A0A',
                fontWeight: 700,
                fontSize: '0.95rem',
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(239, 196, 48, 0.3)',
                '&:hover': {
                  backgroundColor: '#F4D675',
                },
              }}
            >
              Verify & Unlock Admin
            </Button>

            {attempts > 0 && (
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: '#6B7280' }}>
                Failed attempts: {attempts}
              </Typography>
            )}
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
